<template>
  <page :chamber="chamber">
    <slot name="page-title" slot="page-title"></slot>
    <template slot="page-subtitle">
      Last Model Update: <b>{{ sortedDates[sortedDates.length - 1] | readableDate }}</b> | By <a href="https://0ptimus.com" target="_blank">Øptimus</a>
    </template>
    <slot name="page-description" slot="page-description"></slot>
    <v-layout row wrap>
      <v-flex xs12 class="mt-3">
        <loading class="mt-5 mb-5" v-if="loading && landingModels.length === 0" :loading="loading" text="Loading Models"></loading>
        <template v-if="landingModels.length > 0">
          <div class="mt-2">
            <h2 class="section-title mb-4">{{ chamber | capitalize }} Aggregate Simulations</h2>
            <chamber-histogram :chamber="chamber"></chamber-histogram>
          </div>
          <div class="mt-5">
            <h2 class="section-title mb-2">Individual {{ chamber | capitalize }} Seats</h2>
            <div class="section-description mb-4">The chart below displays the probability chance of Republicans winning individual races. The higher the probability, the higher the chance of a Republican win in the race.</div>
            <scatter-table
              :data="landingModels"
              :chamber="chamber"
              :loading="loading">
            </scatter-table>
          </div>
        </template>
        <div class="mb-5" style="text-align: center;" v-if="!loading && landingModels.length === 0">No Landing Models for this Date</div>
      </v-flex>
      <v-flex xs12>
        <opti-card title="MODEL NOTES" :date="convertDate(currentDate)">
          <login-wrapper :content="true">
            <div class="bullet-points" v-html="currentAggregateModel.bulletPoints" v-if="currentAggregateModel"></div>
            <div style="text-align: center;" v-else>No Aggregate Model for this Date</div>
          </login-wrapper>
        </opti-card>
      </v-flex>
    </v-layout>
    <v-layout row wrap style="justify-content: space-between;" class="mt-3">
      <v-flex>
        <h2 class="section-title mb-2 mt-3">{{ chamber | capitalize }} Seats By State</h2>
        <div class="section-description mb-4" v-if="chamber === 'house'">The map below shows the congressional districts for the selected region color coded by GOP win probability (from blue to red, with purple being toss up districts).</div>
        <div class="section-description mb-4" v-if="chamber === 'senate'">The map below shows the GOP win probability (from blue to red, with purple being toss up states) in each state that has a Senate election. If a state has a special election like Minnesota it will be split in half to represent both senate seats up for re-election.</div>
      </v-flex>
    </v-layout>
    <div>
      <template v-if="chamber === 'house'">
        <v-layout row wrap style="display: flex; justify-content: space-between" class="mb-3">
          <div v-if="screenWidth - 280 > 960">
            <div style="text-transform: uppercase; color: black; letter-spacing: 0.12em; font-size: 1.5em; font-weight 400;">{{ currentState.name }}</div>
            <div style="text-transform: uppercase; color: #FA3138; letter-spacing: 0.12em;">Congressional Districts</div>
          </div>
          <states-list v-if="screenWidth - 280 > 960"></states-list>
        </v-layout>
        <v-layout row wrap>
          <template v-if="screenWidth - 280 < 960">
            <v-flex xs12>
              <states-list></states-list>
              <mobile-seat-list chamber="house" :filterState="currentState.name" class="mb-4"></mobile-seat-list>
            </v-flex>
            <v-flex xs12>
              <loading v-if="loading && landingModels.length === 0" class="mt-5" :loading="loading" text="Loading Models"></loading>
              <nationwide-map v-if="landingModels.length > 0" :id="'state-' + chamber + '-map'" :type="chamber" :data="landingModels" :loading="loading" :filterState="currentState.id"></nationwide-map>
              <div style="display: flex; align-items: center;">
                <div id="map-legend-wrapper"></div>
              </div>
            </v-flex>
          </template>
          <template v-else>
            <v-flex xs12 sm12 md3>
              <elections-list :type="chamber" :filterState="currentState.name" :data="landingModels"></elections-list>
              <div style="display: flex; align-items: center;">
                <div id="map-legend-wrapper"></div>
              </div>
            </v-flex>
            <v-flex xs12 sm12 md9>
              <loading v-if="loading && landingModels.length === 0" class="mt-5" :loading="loading" text="Loading Models"></loading>
              <nationwide-map v-if="landingModels.length > 0" :id="'state-' + chamber + '-map'" :type="chamber" :data="landingModels" :loading="loading" :filterState="currentState.id"></nationwide-map>
            </v-flex>
          </template>
        </v-layout>
      </template>
      <template v-if="chamber === 'senate'">
        <v-layout row wrap>
          <template v-if="screenWidth - 280 < 960">
            <v-flex xs12>
              <mobile-seat-list chamber="senate" class="mb-4"></mobile-seat-list>
            </v-flex>
            <v-flex xs12>
              <loading v-if="loading && landingModels.length === 0" class="mt-5" :loading="loading" text="Loading Models"></loading>
              <nationwide-map v-if="landingModels.length > 0" :id="'state-' + chamber + '-map'" :type="chamber" :data="landingModels" :loading="loading"></nationwide-map>
              <div style="display: flex; align-items: center;">
                <div id="map-legend-wrapper"></div>
              </div>
            </v-flex>
          </template>
          <template v-else>
            <v-flex xs3>
              <elections-list :type="chamber" :data="landingModels"></elections-list>
              <div style="display: flex; align-items: center;">
                <div id="map-legend-wrapper"></div>
              </div>
            </v-flex>
            <v-flex xs9>
              <loading v-if="loading && landingModels.length === 0" class="mt-5" :loading="loading" text="Loading Models"></loading>
              <nationwide-map v-if="landingModels.length > 0" :id="'state-' + chamber + '-map'" :type="chamber" :data="landingModels" :loading="loading"></nationwide-map>
            </v-flex>
          </template>
        </v-layout>
      </template>
    </div>
  </page>
</template>

<script>
import cloneDeep from 'lodash.clonedeep'
import { mapActions, mapGetters } from 'vuex'

import { NationwideMap, ScatterTable } from '@/components/charts'
import Loading from '@/components/common/Loading'
import { OptiCard } from '@/components/optimus'
import ElectionsList from './ElectionsList'
import StatesList from './StatesList'
import MobileSeatList from './MobileSeatList'
import Page from './Page'

const name = 'landing_page_wrapper'

const props = {
  chamber: {
    type: String,
    required: false
  },
  data: {
    type: Array,
    required: false
  }
}

const computed = {
  ...mapGetters({
    houseLandingModels: 'landingModels/houseList',
    senateLandingModels: 'landingModels/senateList',
    currentDate: 'aggregateModels/currentDate',
    houseDate: 'aggregateModels/houseDate',
    senateDate: 'aggregateModels/senateDate',
    currentState: 'elections/currentState',
    aggregateModels: 'aggregateModels/list',
    elections: 'elections/list',
    mobile: 'ux/mobile',
    screenWidth: 'ux/screenWidth',
    dates: 'aggregateModels/dates'
  }),
  currentAggregateModel () {
    return this.aggregateModels.filter(model => model.chamber === this.chamber.toUpperCase()).find(model => model.date === this.currentDate)
  },
  senateElections () {
    const elections = this.elections.filter(election => { return election.raceId.includes('SENATE') })
    elections.sort((a, b) => { return a.state.localeCompare(b.state) })
    return elections
  },
  landingModels () {
    if (this.chamber === 'house') {
      return this.houseLandingModels
    } else if (this.chamber === 'senate') {
      return this.senateLandingModels
    }
  },
  sortedDates () {
    const dates = cloneDeep(this.dates)
    return dates.sort((a, b) => {
      return a.localeCompare(b)
    })
  }
}

const methods = {
  ...mapActions({
    getHouseLandingModels: 'landingModels/houseList',
    getSenateLandingModels: 'landingModels/senateList',
    setHouseDate: 'aggregateModels/setHouseDate',
    setSenateDate: 'aggregateModels/setSenateDate'
  }),
  getModels () {
    this.loading = true
    const params = {
      date: this.currentDate,
      chamber: this.chamber.toUpperCase()
    }
    if (this.chamber === 'house' && params.date) {
      this.getHouseLandingModels(params).then(() => {
        this.loading = false
      })
    } else if (this.chamber === 'senate' && params.date) {
      this.getSenateLandingModels(params).then(() => {
        this.loading = false
      })
    }
  },
  sliderUpdate (bounds) {
    this.bounds = bounds
    this.getModels()
  },
  convertDate (date) {
    if (!date) return ''
    const split = date.split('-')
    const newDate = split[1] + '/' + split[2]
    return newDate
  },
  getListItemStyle (model) {
    if (this.hoveredPoint === model.election.state && !model.election.specialElection) {
      return 'color: red;'
    } else if (model.election.specialElection && this.hoveredPoint === model.election.state + ' (Special)') {
      return 'color: red;'
    }
  }
}

const watch = {
  currentDate () {
    this.getModels()
  }
}

const components = {
  'chamber-histogram': () => import('./common/ChamberHistogram'),
  ElectionsList,
  Loading,
  'login-wrapper': () => import('./common/LoginWrapper'),
  MobileSeatList,
  NationwideMap,
  OptiCard,
  Page,
  ScatterTable,
  StatesList
}

export default {
  name,
  props,
  computed,
  methods,
  watch,
  components,
  data () {
    return {
      loading: false,
      bounds: [40, 60],
      chartHeight: null,
      hoveredPoint: '',
      hoveredItem: ''
    }
  },
  beforeMount () {
    if (this.chamber === 'house' &&
      (this.houseLandingModels.length === 0 || this.houseDate !== this.currentDate)) {
      this.setHouseDate(this.currentDate)
      this.getModels()
    } else if (this.chamber === 'senate' &&
      (this.senateLandingModels.length === 0 || this.senateDate !== this.currentDate)) {
      this.setSenateDate(this.currentDate)
      this.getModels()
    }
  }
}
</script>

<style>
#map-legend-wrapper {
  width: 100%;
  height: 100px;
  position: relative;
  margin: 20px 0;
}
</style>



// WEBPACK FOOTER //
// src/components/LandingPageWrapper.vue